import i18nService, { t } from '../i18nService';

import stateCache from '../StateCache';
import storeManager from '../../store/StoreManager';
import sessionActions from '../../actions/SessionActions';
import sessionConstants from '../../constants/SessionConstants';
import UploadService from './UploadService';
import CodeCompilerService from './CodeCompilerService';
import PCIService from './PCIService';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import globalService from '../GlobalsService';
import { reportError } from '../ErrorReporting';
import environmentNames from '../../environmentNames';
import qtiConstants from '../../qti/Constants';
import questionSelectionService from '../QuestionSelectionService';
import scorableItemNumberingService from './ScorableItemNumberingService';
import feedbackService from '../FeedbackService';
import navigationService from '../NavigationService';
import utilService from '../UtilService';

const questionHelperServices = {
    getServices() {
        const translationService = {
            t,
        };

        const stateCacheInstance = stateCache.getStateCacheInstance();
        const stateCacheService = {
            setQuestionCacheValue(keyName, value) {
                stateCacheInstance.setQuestionCacheValue(keyName, value);
            },
            getQuestionCacheValue(keyName) {
                return stateCacheInstance.getQuestionCacheValue(keyName);
            },
            removeQuestionCacheValue(keyName) {
                return stateCacheInstance.removeQuestionCacheValue(keyName);
            },
        };

        const interactionService = {
            activeMultimedia: undefined,
            fileUploadInProgress: undefined,

            canResetMultimediaPlayedTimes() {
                return (!sessionReader.userHasLockDown() &&
                        (sessionReader.isContextPreview() || globalService.isLocalhost()));
            },

            setMultimediaStatus(isPlaying, identifier, enablePauseMode) {
                const params = isPlaying
                    ? [isPlaying, sessionConstants.LOCK.MULTIMEDIA_PLAYING, `interaction:${identifier}`, enablePauseMode]
                    : [false];
                storeManager.dispatch(
                    sessionActions.toggleNavigationLock(...params),
                );

                this.activeMultimedia = isPlaying ? identifier : undefined;
            },

            isMultimediaPlaying() {
                return (this.activeMultimedia !== undefined);
            },

            getActiveMultimediaIdentifier() {
                return this.activeMultimedia;
            },

            isSpellCheckerEnabled() {
                const candidateSetting = storeManager.getState().settings.values.spellCheckerDisabledCandidate;
                return (typeof candidateSetting !== 'undefined') ?
                    !candidateSetting :
                    !storeManager.getState().settings.values.spell_checker_disabled;
            },

            isSketchesEnabled() {
                const candidateSetting = storeManager.getState().settings.values.isSketchesEnabledCandidate;
                return (typeof candidateSetting !== 'undefined') ?
                    candidateSetting :
                    storeManager.getState().settings.values.enable_sketches;
            },

            setFileUploadStatus(inProgress, identifier) {
                const params = inProgress
                    ? [inProgress, sessionConstants.LOCK.FILE_UPLOAD_IN_PROGRESS, `interaction:${identifier}`]
                    : [false];
                storeManager.getStore()
                    .dispatch(
                        sessionActions.toggleNavigationLock(...params),
                    );

                this.fileUploadInProgress = inProgress ? identifier : undefined;
            },

            isFileUploadInProgress() {
                return (this.fileUploadInProgress !== undefined);
            },

            getFileUploadOwnerId() {
                return this.fileUploadInProgress;
            },

            /**
             * Gets the maximum allowed file upload size in bytes.
             * @returns {int}
             */
            getFileUploadMaxSize() {
                return settingsReader.getMaxFileUploadSize();
            },

            shouldShowPrompt(prompt) {
                return !(!prompt || !prompt.content);
            },

            reportError(...args) {
                reportError(args);
            },
        };

        const uploadService = {
            /**
             * Uploads a file, and notifies the caller of progress, success, error etc. via the callback function.
             * @param {object} file
             * @param questionId
             * @param contentRevisionId
             * @param callback Function to use for passing update notifications to the interaction.
             * @returns {{cancel, stopListening}|*}
             */
            upload(file, questionId, contentRevisionId, callback, skipContentUrlCreation = false) {
                return UploadService.uploadAssignmentFile(file, questionId, contentRevisionId, callback, skipContentUrlCreation);
            },

            /**
             * @param {string|int} contentItemId of the uploaded file.
             * @returns {string}
             */
            getFileDownloadUrl(contentItemId) {
                return `/file/ci/${contentItemId}/file`;
            },
        };

        const codeCompilerService = {
            submitCodeForCompilation(questionId, sourceCode, checkAgainstEvaluationTestCases) {
                return CodeCompilerService.submitCodeForCompilation(questionId, sourceCode, checkAgainstEvaluationTestCases);
            },
        };

        const pdfJsService = {
            getPdfJsUrl(url) {
                const isDevelopment = (process.env.NODE_ENV === environmentNames.DEVELOPMENT);
                const pdfUrl = isDevelopment ? qtiConstants.staticLibPaths.DEVELOPMENT_PDF : url;
                const pdfJsURL = `${qtiConstants.staticLibPaths.PDFJS}?file=${encodeURIComponent(pdfUrl)}`;
                const sebFlag = sessionReader.userHasLockDown() ? '&disablePDFDownload=true&disablePrint=true' : '';

                return `${pdfJsURL}${sebFlag}`;
            },

            getOfflinePdfJsUrl(offlineFileName) {
                return `${window.offlinePdfJSPath}${offlineFileName}.pdf`;
            },
        };

        const pciService = Object.assign({}, PCIService);

        return {
            i18nService,
            interactionService,
            stateCacheService,
            translationService,
            uploadService,
            codeCompilerService,
            pdfJsService,
            pciService,
            questionSelectionService,
            scorableItemNumberingService,
            navigationService,
            utilService,
            feedbackService,
        };
    },
};

export default questionHelperServices;
